// Outlet — mirrors libs/pkmdw/presenter/outlet.go
export interface IOutlet {
    id: string
    code: string
    name: string
    phone: string
    address: string
    region: string
    province: string
    district: string
    ward: string
    outletType: string
    segment: string
    status: string
    cpCode: string
    parentOutletId: string
    ownerUserId: number
    ownerUserName?: string
    parentOutletName?: string
    expectedMonthly: number
    competitors: string
    rejectReason: string
    firstSeenDate?: string | null
    note: string
    createdAt: string
    updatedAt: string
    createdByUserId: number
    updatedByUserId: number
}

export interface ICreateOutletRequest {
    name: string
    code?: string
    phone?: string
    address?: string
    region?: string
    province?: string
    district?: string
    ward?: string
    outletType?: string
    segment?: string
    status?: string
    cpCode?: string
    parentOutletId?: string
    ownerUserId?: number
    expectedMonthly?: number
    competitors?: string
    rejectReason?: string
    firstSeenDate?: string
    note?: string
}

export interface IUpdateOutletRequest {
    id: string
    name?: string
    code?: string
    phone?: string
    address?: string
    region?: string
    province?: string
    district?: string
    ward?: string
    outletType?: string
    segment?: string
    status?: string
    cpCode?: string
    parentOutletId?: string
    ownerUserId?: number
    expectedMonthly?: number
    competitors?: string
    rejectReason?: string
    firstSeenDate?: string
    note?: string
}

export interface IFilterOutletRequest {
    search?: string
    cpCodes?: string[]
    provinces?: string[]
    districts?: string[]
    statuses?: string[]
    outletTypes?: string[]
    segments?: string[]
    ownerUserIds?: number[]
    parentOutletId?: string
    hasParent?: boolean
    offset?: number
    limit?: number
    orderBy?: string
}

export interface IListOutletsResponse {
    data: IOutlet[]
    total: number
}

export interface ICreateOutletResponse {
    id: string
}

// Outlet inventory link — mirrors libs/pkmdw/presenter/outlet_inventory_link.go
export interface IOutletInventoryLink {
    id: string
    simInventoryId: number
    outletId: string
    outletName?: string
    startSim?: string
    endSim?: string
    totalSim?: number
    orderId?: string
    simStatus?: string
    linkedAt: string
    linkedByUserId: number
    source: string
    note: string
    createdAt: string
    updatedAt: string
}

export interface ICreateOutletLinkRequest {
    simInventoryId: number
    outletId: string
    source?: string
    note?: string
}

export interface IUpdateOutletLinkRequest {
    id: string
    linkedAt?: string
    linkedByUserId?: number
    note?: string
}

export interface IFilterOutletLinkRequest {
    outletId?: string
    outletIds?: string[]
    simInventoryIds?: number[]
    linkedByUserIds?: number[]
    sources?: string[]
    fromDate?: string
    toDate?: string
    offset?: number
    limit?: number
}

export interface IListOutletLinksResponse {
    data: IOutletInventoryLink[]
    total: number
}

// Outlet order wrapper
export interface ISimInventoryInfo {
    startSim: string
    endSim: string
}

export interface IAddOrderInventoryRequest {
    cpCode: string
    description: string
    simInventory: ISimInventoryInfo[]
}

export interface ICreateOutletOrderRequest {
    outletId: string
    order: IAddOrderInventoryRequest
    note?: string
}

export interface ICreateOutletOrderResponse {
    order: any
    linkIds: string[]
    outletId: string
}

export interface ILinkExistingOrderRequest {
    outletId: string
    orderId: string
    note?: string
}

export interface ILinkExistingOrderResponse {
    outletId: string
    orderId: string
    linkIds: string[]
    skippedSimIds?: number[]
}

// Outlet activity — mirrors libs/pkmdw/presenter/outlet_activity.go
export interface IOutletActivity {
    id: string
    outletId: string
    type: string
    note: string
    attachmentUrl: string
    nextFollowupAt?: string | null
    outcome: string
    durationMinutes: number
    actorUserId: number
    actorUserName?: string
    createdAt: string
    updatedAt: string
}

export interface ICreateActivityRequest {
    outletId: string
    type: string
    note?: string
    attachmentUrl?: string
    nextFollowupAt?: string
    outcome?: string
    durationMinutes?: number
    actorUserId: number
}

export interface IUpdateActivityRequest {
    id: string
    note?: string
    nextFollowupAt?: string
    outcome?: string
    durationMinutes?: number
}

export interface IFilterActivityRequest {
    outletId?: string
    outletIds?: string[]
    types?: string[]
    actorUserIds?: number[]
    outcomes?: string[]
    fromDate?: string
    toDate?: string
    offset?: number
    limit?: number
    orderBy?: string
}

export interface IUpcomingFollowupRequest {
    withinDays?: number
    actorUserIds?: number[]
    limit?: number
}

export interface IListActivitiesResponse {
    data: IOutletActivity[]
    total: number
}

// Outlet pricing — mirrors libs/pkmdw/presenter/outlet_pricing.go
export interface IPricingAgreement {
    id: string
    outletId: string
    partnerPackageId: number
    packageCode: string
    packageCpCode: string
    agreedPrice?: number | null
    agreedCommission?: number | null
    defaultPrice?: number | null
    defaultCommission?: number | null
    agreedNote: string
    effectiveFrom: string
    effectiveTo?: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
}

export interface ICreatePricingRequest {
    outletId: string
    partnerPackageId: number
    agreedPrice?: number
    agreedCommission?: number
    agreedNote?: string
    effectiveFrom: string
    effectiveTo?: string
    closeActive?: boolean
}

export interface IUpdatePricingRequest {
    id: string
    agreedPrice?: number
    agreedCommission?: number
    agreedNote?: string
    effectiveTo?: string
}

export interface IFilterPricingRequest {
    outletId?: string
    outletIds?: string[]
    partnerPackageIds?: number[]
    packageCpCodes?: string[]
    activeOnly?: boolean
    offset?: number
    limit?: number
}

export interface IGetActivePricingRequest {
    outletId: string
    partnerPackageId: number
}

export interface IListPricingResponse {
    data: IPricingAgreement[]
    total: number
}

// Values must match backend IsValid* validators.
export const OUTLET_STATUS_OPTIONS = [
    { value: 'lead', label: 'Tiềm năng' },
    { value: 'contacted', label: 'Đã tiếp xúc' },
    { value: 'consigning', label: 'Đang ký gửi' },
    { value: 'active', label: 'Đang hoạt động' },
    { value: 'dormant', label: 'Không hoạt động' },
    { value: 'rejected', label: 'Từ chối hợp tác' },
]

export const OUTLET_TYPE_OPTIONS = [
    { value: 'gara', label: 'Gara' },
    { value: 'dau_moi_sim', label: 'Đầu mối SIM' },
    { value: 'kenh_truc_tiep', label: 'Kênh trực tiếp' },
]

export const OUTLET_SEGMENT_OPTIONS = [
    { value: 'do_xe', label: 'Độ xe' },
    { value: 'noi_that', label: 'Nội thất' },
    { value: 'cham_soc_xe', label: 'Chăm sóc xe' },
    { value: 'do_choi_xe_hoi', label: 'Đồ chơi xe hơi' },
    { value: 'sim_data', label: 'SIM Data' },
]

export const ACTIVITY_TYPE_OPTIONS = [
    { value: 'visit', label: 'Ghé thăm' },
    { value: 'call', label: 'Gọi điện' },
    { value: 'note', label: 'Ghi chú' },
    { value: 'reject', label: 'Bị từ chối' },
    { value: 'sms', label: 'Nhắn tin' },
]

export const ACTIVITY_OUTCOME_OPTIONS = [
    { value: 'success', label: 'Thành công' },
    { value: 'no_answer', label: 'Không phản hồi' },
    { value: 'postponed', label: 'Hoãn lại' },
    { value: 'rejected', label: 'Bị từ chối' },
    { value: 'other', label: 'Khác' },
]

export interface IOutletStatsRequest {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
}

export interface ITopOutlet {
    outletId: string
    outletName: string
    province: string
    simAllocated: number
    simActivated: number
    renewalCount: number
    revenue: number
    revenueActivation?: number
    revenueRenewal?: number
}

export interface IOutletStatsResponse {
    outletsByStatus: Record<string, number>
    totalOutlets: number
    totalSimAllocated: number
    totalSimActivated: number
    totalRenewalCount: number
    activationRate: number
    totalRevenue: number
    totalRevenueActivation?: number
    totalRevenueRenewal?: number
    topOutlets: ITopOutlet[]
}

export interface IOutletRegionReportRequest {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
    groupBy?: 'province' | 'district'
}

export interface IRegionRow {
    province: string
    district?: string
    outletCount: number
    simAllocated: number
    simActivated: number
    renewalCount: number
    activationRate: number
    revenue: number
    overdueCount: number
}

export interface IOutletRegionReportResponse {
    data: IRegionRow[]
}

export interface IOutletSaleEfficiencyRequest {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
    ownerUserIds?: number[]
}

export interface ISaleRow {
    ownerUserId: number
    ownerUserName?: string
    outletCount: number
    activeOutlets: number
    simAllocated: number
    simActivated: number
    renewalCount: number
    activationRate: number
    revenue: number
    conversionRate: number
}

export interface IOutletSaleEfficiencyResponse {
    data: ISaleRow[]
}

export interface IOutletOverdueRequest {
    overdueDays?: number
    cpCodes?: string[]
    outletIds?: string[]
    ownerUserIds?: number[]
    provinces?: string[]
    offset?: number
    limit?: number
}

export interface IOverdueRow {
    outletId: string
    outletName: string
    province: string
    district?: string
    ownerUserId: number
    ownerUserName?: string
    simInventoryId: number
    startSim: string
    endSim: string
    totalSim: number
    totalActive: number
    remainingSim: number
    linkedAt: string
    daysHeld: number
}

export interface IOutletOverdueResponse {
    data: IOverdueRow[]
    total: number
}

export interface IOutletGeoOptionsRequest {
    cpCodes?: string[]
}

export interface IOutletGeoOptionsResponse {
    provinces: string[]
    districtsByProvince: Record<string, string[]>
}

export interface IActivationHistoryRequest {
    outletId: string
    types?: string[]
    status?: string
    fromDate?: string
    toDate?: string
    offset?: number
    limit?: number
}

export interface ITransactionConnectionView {
    id: string
    cpCode: string
    ownerBy: string
    phoneAgency: string
    phoneSim: string
    serialSim: string
    phoneCustomer: string
    dataPack: string
    numberDay: number
    type: string
    price: number
    startDate: number
    endDate: number
    status: string
    isuse: boolean
    extensionCount: number
    connectionDate: number
    createdAt: string
    updatedAt: string
}

export interface IActivationHistoryResponse {
    data: ITransactionConnectionView[]
    total: number
    totalActivations: number
}

export const TC_TYPE_LABELS: Record<string, string> = {
    CONNECTION: 'Đấu nối',
    EXTENSION: 'Gia hạn',
}
